import { useEffect, useState } from 'react';
import {
    Button,
    Box,
    Grid,
    Tooltip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Snackbar,
    Alert,
    Stack
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { AddAddressDialog } from './AddAddress';
import { DataGrid, GridRenderCellParams } from '@mui/x-data-grid';
import { getClientInfo, getInternalUserAddressLocations, getExternalUserAddress, addAddress, getAllStates, updateAddress, deleteAddress } from 'services';

export const ManageAddress = ({ selectedClientId, refreshKey }: any) => {
    const [paginationModel, setPaginationModel] = useState({
        page: 1,
        pageSize: 10,
        total: 0,
        data: [],
        isLoading: false
    });
    const [editRow, setEditRow] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [addressToDelete, setAddressToDelete] = useState<any>(null);
    const [successMessageStatus, setSuccessMessageStatus] = useState(false);
    const [errorMessageStatus, setErrorMessageStatus] = useState(false);
    const [responseMessage, setResponseMessage] = useState('');
    const [getAllState, setGetAllState] = useState([]);
    const [clientInfo, setClientInfo] = useState([]);

    const jwt: any = localStorage.getItem('token');
    const labUserCheck: any = jwt ? JSON.parse(atob(jwt.split('.')[1])) : null;

    const columns = [
        {
            field: 'name',
            headerName: 'Recipient/Business Name',
            width: 200
        },
        {
            field: 'street',
            headerName: 'Building Number/ Street',
            width: 200
        },
        {
            field: 'postalCode',
            headerName: 'Postal Code',
            width: 150
        },
        {
            field: 'state',
            headerName: 'State',
            width: 150
        },
        { field: 'country', headerName: 'Country', width: 150 },
        {
            field: 'isDefault',
            headerName: 'Status',
            width: 150,
            renderCell: (params: GridRenderCellParams) => (
                <span>
                    {labUserCheck?.UserType === 'Internal'
                        ? (params.row.defaultClientAddress ? 'Default' : '')
                        : (params.row.defaultCustomerAddress ? 'Default' : '')}
                </span>
            )
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 150,
            renderCell: (params: GridRenderCellParams) => (
                <>
                    <Tooltip title="Edit">
                        <IconButton
                            color="primary"
                            size="small"
                            disabled={!params.row.isEditable}
                            onClick={() => {
                                setEditRow(params.row);
                                setOpenDialog(true);
                            }}
                        >
                            <EditOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton
                            color="error"
                            size="small"
                            disabled={!params.row.isEditable}
                            onClick={() => {
                                setAddressToDelete(params.row);
                                setDeleteDialogOpen(true);
                            }}
                        >
                            <DeleteOutlineOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                </>
            )
        }
    ];

    useEffect(() => {
        getUserAddressApi();
    }, [paginationModel.page, selectedClientId, labUserCheck?.UserType]);

    useEffect(() => {
        setPaginationModel(prev => ({
            ...prev,
            page: 1
        }));
    }, [refreshKey]);

    useEffect(() => {
        getAllStateApi();
    }, []);

    useEffect(() => {
        getClientInfoApi();
    }, [editRow]);

    const getClientInfoApi = () => {
        if (labUserCheck?.UserType === 'Internal' && editRow?.clientID) {
            getClientInfo(editRow?.clientID).then((res: any) => {
                if (res.status === 200) {
                    setClientInfo(res.data);
                }
            })
        }
    }

    const getUserAddressApi = () => {
        if (labUserCheck?.UserType !== 'Internal') {
            if (selectedClientId) {
                getExternalUserAddress(selectedClientId).then((res: any) => {
                    if (res?.status === 200) {
                        setPaginationModel(prev => ({
                            ...prev,
                            total: res?.total,
                            data: res?.data
                        }));
                    } else {
                        setErrorMessageStatus(true);
                        setResponseMessage(res?.data);
                    }
                })
            }
        } else {
            internalUserAddressApi();
        }
    }

    const internalUserAddressApi = () => {
        let pageName = 'manageAddress';
        getInternalUserAddressLocations(paginationModel.page, selectedClientId, pageName).then((res: any) => {
            if (res?.status === 200) {
                setPaginationModel(prev => ({
                    ...prev,
                    total: res?.data?.totalCount,
                    data: res?.data?.despatchAddresses
                }));
            } else {
                setErrorMessageStatus(true);
                setResponseMessage(res?.data);
            }
        })
    }

    const addNewAddressApi = (data: any) => {
        const { isDefault, ...restData } = data;
        const payload = {
            ...restData,
            clientID: labUserCheck?.UserType !== 'Internal' ? selectedClientId : data.clientID,
            defaultClientAddress: labUserCheck?.UserType === 'Internal' ? !!data.defaultClientAddress : false,
            defaultCustomerAddress: labUserCheck?.UserType !== 'Internal' ? !!data.defaultCustomerAddress : false
        };
        addAddress(payload).then((res: any) => {
            if (res.status === 200) {
                setSuccessMessageStatus(true);
                setResponseMessage("Address added successfully!");
                setOpenDialog(false);
                getUserAddressApi();
            } else {
                setErrorMessageStatus(true);
                setResponseMessage(res.data);
            }
        })
    }

    const getAllStateApi = () => {
        getAllStates().then((res: any) => {
            if (res.status === 200) {
                setGetAllState(res.data);
            } else {
                setErrorMessageStatus(true);
                setResponseMessage(res.data);
            }
        })
    }

    const updateAddressApi = (Obj: any) => {
        updateAddress(Obj).then((res: any) => {
            if (res.status === 200) {
                setSuccessMessageStatus(true);
                setResponseMessage("Address updated successfully!");
                setOpenDialog(false);
                getUserAddressApi();
            } else {
                setOpenDialog(false);
                setErrorMessageStatus(true);
                setResponseMessage(res.data);
            }
        })
    }

    const deleteAddressApi = (Obj: any) => {
        deleteAddress(Obj).then((res: any) => {
            if (res.status === 200) {
                setSuccessMessageStatus(true);
                setResponseMessage("Address deleted successfully!");
                setOpenDialog(false);
                getUserAddressApi();
            } else {
                setErrorMessageStatus(true);
                setResponseMessage(res.message);
            }
        })
    }

    const handleAddAddress = (data: any) => {
        if (editRow) {
            updateAddressApi(data);
        } else {
            addNewAddressApi(data);
        }
        setEditRow(null);
    };

    const labelNames = [
        {
            label: 'Recipient/Business Name',
            name: 'name'
        },
        ...(labUserCheck?.UserType === 'Internal' ? [{
            label: 'Client Name',
            name: 'clientName'
        }] : []),
        {
            label: 'Building Number/ Street',
            name: 'street'
        },
        {
            label: 'Postal Code',
            name: 'postalCode'
        },
        {
            label: 'Suburb',
            name: 'suburb'
        },
        {
            label: 'State',
            name: 'state'
        },
        {
            label: 'Country',
            name: 'country'
        },
        {
            label: 'Status',
            name: 'status'
        }
    ]

    return (
        <>
            <Box display="flex" justifyContent="flex-end">
                <Button variant="contained" color="primary" onClick={() => setOpenDialog(true)}>
                    Add Address
                </Button>
            </Box>
            <Grid container spacing={2} mt={1}>
                <Grid item xs={12} sm={12}>
                    <DataGrid
                        autoHeight
                        sx={{
                            '.MuiDataGrid-columnHeaders': {
                                backgroundColor: '#003883',
                                color: '#fff'
                            },
                            '.MuiDataGrid-menuIcon .MuiSvgIcon-root': {
                                color: '#fff'
                            },
                            '.MuiDataGrid-iconButtonContainer .MuiSvgIcon-root': {
                                color: '#fff'
                            }
                        }}
                        columns={columns}
                        rows={paginationModel?.data}
                        rowCount={paginationModel?.total}
                        loading={paginationModel?.isLoading}
                        page={paginationModel?.page - 1}
                        pageSize={paginationModel?.pageSize}
                        pagination
                        paginationMode="server"
                        onPageChange={(newPage: any) => {
                            setPaginationModel(old => ({ ...old, page: newPage + 1 }))
                        }}
                    />
                </Grid>
            </Grid>
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>{editRow ? 'Update Address' : 'Add Address'}</DialogTitle>
                <DialogContent>
                    <AddAddressDialog
                        labelNames={labelNames}
                        onSave={handleAddAddress}
                        onClose={() => {
                            setOpenDialog(false);
                            setEditRow(null);
                        }}
                        initialValues={editRow}
                        getAllState={getAllState}
                        clientInfo={clientInfo}
                    />
                </DialogContent>
            </Dialog>
            <Dialog
                open={deleteDialogOpen}
                onClose={() => {
                    setDeleteDialogOpen(false);
                    setAddressToDelete(null);
                }}
            >
                <DialogTitle>Delete Address</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete the address?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setDeleteDialogOpen(false);
                            setAddressToDelete(null);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={() => {
                            if (addressToDelete?.id) {
                                deleteAddressApi(addressToDelete.id);
                            }
                            setDeleteDialogOpen(false);
                            setAddressToDelete(null);
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            <Stack sx={{ width: '100%', marginTop: 2 }} spacing={2}>
                <Snackbar open={successMessageStatus} autoHideDuration={6000} onClose={() => setSuccessMessageStatus(false)}>
                    <Alert severity="success" onClose={() => setSuccessMessageStatus(false)}><b>{responseMessage}</b></Alert>
                </Snackbar>
                <Snackbar open={errorMessageStatus} autoHideDuration={6000} onClose={() => setErrorMessageStatus(false)}>
                    <Alert severity="error" onClose={() => setErrorMessageStatus(false)}><b>{responseMessage}</b></Alert>
                </Snackbar>
            </Stack>
        </>
    );
}
